import { useMemo, useState } from "react";
import { Alert, Empty, Spin } from "antd";
import { useSelector } from "react-redux";
import dayjsTZ from "../../utils/dayjs";
import AnalyticsFilterBar from "./filters/AnalyticsFilterBar";
import useAnalyticsData from "./hooks/useAnalyticsData";
import KpiStrip from "./components/KpiStrip";
import HoursOverTimeChart from "./components/HoursOverTimeChart";
import HoursMixChart from "./components/HoursMixChart";
import DeptTeamCharts from "./components/DeptTeamCharts";
import HorizontalBarChart from "./components/HorizontalBarChart";
import { CHART_COLORS } from "./utils/chartTheme";
import "./AnalyticsPage.scss";

function defaultFilters() {
  const now = dayjsTZ();
  return {
    year: now.year(),
    quarter: null,
    month: now.month(),
    department: null,
    team: null,
    employeeId: null,
    employeeScope: "active",
  };
}

function filtersEqual(a, b) {
  return (
    a?.year === b?.year &&
    a?.quarter === b?.quarter &&
    a?.month === b?.month &&
    a?.department === b?.department &&
    a?.team === b?.team &&
    a?.employeeId === b?.employeeId &&
    a?.employeeScope === b?.employeeScope
  );
}

export default function AnalyticsPage() {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const initial = useMemo(() => defaultFilters(), []);
  const [draftFilters, setDraftFilters] = useState(initial);
  const [appliedFilters, setAppliedFilters] = useState(initial);
  const { loading, error, dashboard, filterOptions, range } =
    useAnalyticsData(appliedFilters);

  const periodLabel = useMemo(() => {
    if (!range?.[0] || !range?.[1]) return "";
    return `${range[0].format("DD/MM/YYYY")} – ${range[1].format("DD/MM/YYYY")}`;
  }, [range]);

  const applyDisabled = filtersEqual(draftFilters, appliedFilters);

  return (
    <div
      className={`analytics-page globalPageWrapper${
        isDarkMode ? " dark" : ""
      }`}
    >
      <header className="analytics-page__header">
        <div className="analytics-page__heading">
          <h1>Analitika</h1>
          <p className="analytics-page__period">{periodLabel}</p>
        </div>
        <AnalyticsFilterBar
          value={draftFilters}
          onChange={setDraftFilters}
          onApply={() => setAppliedFilters(draftFilters)}
          applyDisabled={applyDisabled}
          loading={loading}
          filterOptions={filterOptions}
          isDarkMode={isDarkMode}
        />
      </header>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Nuk u ngarkuan të dhënat e prezencës"
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Spin spinning={loading}>
        {!loading &&
        !dashboard?.kpis?.totalHours &&
        !dashboard?.leaveByType?.length ? (
          <Empty description="Nuk ka të dhëna për periudhën e zgjedhur" />
        ) : (
          <>
            <KpiStrip kpis={dashboard.kpis} />

            <h2 className="analytics-section-title">Ore & përzierje</h2>
            <div className="analytics-grid analytics-grid--2">
              <HoursOverTimeChart
                data={dashboard.hoursOverTime}
                isDarkMode={isDarkMode}
              />
              <HoursMixChart
                data={dashboard.hoursMix}
                isDarkMode={isDarkMode}
              />
            </div>

            <h2 className="analytics-section-title">Departamente & ekipe</h2>
            <DeptTeamCharts
              byDepartment={dashboard.byDepartment}
              byTeam={dashboard.byTeam}
              isDarkMode={isDarkMode}
            />

            <h2 className="analytics-section-title">Punonjësit</h2>
            <div className="analytics-grid analytics-grid--2">
              <HorizontalBarChart
                title="Ore më të larta"
                data={dashboard.topHours}
                dataKey="total"
                color={CHART_COLORS.teal}
                isDarkMode={isDarkMode}
                unit="h"
              />
              <HorizontalBarChart
                title="Lidership OT"
                data={dashboard.topOt}
                dataKey="value"
                color={CHART_COLORS.yellow}
                isDarkMode={isDarkMode}
                unit="h"
              />
              <HorizontalBarChart
                title="Vonesa (minuta)"
                data={dashboard.lateLeaders}
                dataKey="value"
                color={CHART_COLORS.red}
                isDarkMode={isDarkMode}
                unit="min"
              />
            </div>

            <h2 className="analytics-section-title">Pushimet</h2>
            <div className="analytics-grid analytics-grid--2">
              <HorizontalBarChart
                title="Sipas llojit"
                data={dashboard.leaveByType}
                dataKey="value"
                color={CHART_COLORS.blue}
                isDarkMode={isDarkMode}
                unit="ditë"
              />
              <HorizontalBarChart
                title="Përdoruesit kryesorë"
                data={dashboard.leaveTopUsers}
                dataKey="value"
                color={CHART_COLORS.blueSoft}
                isDarkMode={isDarkMode}
                unit="ditë"
              />
            </div>
          </>
        )}
      </Spin>
    </div>
  );
}
